import { useState, useEffect } from "react";
import { Download, X } from "lucide-react";

let deferredPrompt = null;

export default function PWAInstallPrompt() {
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    setIsStandalone(standalone);
    if (standalone) return;

    // Check if dismissed recently
    const dismissed = localStorage.getItem("pwa-dismissed");
    if (dismissed && Date.now() - parseInt(dismissed) < 3 * 24 * 60 * 60 * 1000) return;

    // iOS detection
    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    setIsIOS(ios);

    // Android/Chrome install prompt
    const handler = (e) => {
      e.preventDefault();
      deferredPrompt = e;
      setShowBanner(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Show iOS banner after delay
    if (ios) {
      setTimeout(() => setShowBanner(true), 3000);
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === "accepted") {
        setShowBanner(false);
      }
      deferredPrompt = null;
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem("pwa-dismissed", Date.now().toString());
  };

  if (!showBanner || isStandalone) return null;

  return (
    <div className="fixed bottom-20 left-3 right-3 z-[100] animate-slide-up" data-testid="pwa-install-banner">
      <div className="bg-gradient-to-r from-[#0284C7] to-[#0369A1] rounded-2xl p-4 shadow-xl shadow-[#0284C7]/20 flex items-center gap-3">
        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
          <Download size={24} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-bold">Install App</p>
          {isIOS ? (
            <p className="text-white/80 text-[11px] mt-0.5">
              Tap <span className="inline-block w-4 h-4 align-middle"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-full h-full"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/></svg></span> then "Add to Home Screen"
            </p>
          ) : (
            <p className="text-white/80 text-[11px] mt-0.5">Get quick access from your home screen</p>
          )}
        </div>
        {!isIOS && deferredPrompt && (
          <button
            onClick={handleInstall}
            className="bg-white text-[#0284C7] text-xs font-bold px-4 py-2 rounded-lg flex-shrink-0"
            data-testid="pwa-install-btn"
          >
            Install
          </button>
        )}
        <button onClick={handleDismiss} className="text-white/60 p-1 flex-shrink-0" data-testid="pwa-dismiss-btn">
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
